import React from 'react';
import { PALETTE } from './SiteLayout';

/**
 * Founder credibility block, home page.
 * Photo + paragraph + affiliation badges (BCS / ICS / MIMCA / DPO).
 */
export default function FounderBlock() {
  const affiliations = [
    { src: '/team/affiliations/bcs.jpg', label: 'British Computer Society, Professional Member' },
    { src: '/team/affiliations/ics.png', label: 'Irish Computer Society, Member' },
    { src: '/team/affiliations/mimca.png', label: 'IT Asset Manager (ITAM)' },
    { src: '/team/affiliations/dpo.png', label: 'Data Protection Officers Association, Ireland' },
    { src: '/team/affiliations/hisi.png', label: 'Healthcare Informatics Society of Ireland' },
  ];

  return (
    <section
      data-testid="founder-credibility-block"
      style={{
        background: PALETTE.paper,
        padding: '120px 32px',
        borderTop: `1px solid ${PALETTE.border}`,
      }}
    >
      <div style={{ maxWidth: 1080, margin: '0 auto' }}>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: '320px 1fr',
            gap: 64,
            alignItems: 'center',
          }}
          className="founder-row"
        >
          {/* Photo */}
          <div className="founder-photo-wrap">
            <div style={{
              position: 'relative',
              borderRadius: 8,
              overflow: 'hidden',
              border: `1px solid ${PALETTE.border}`,
              boxShadow: '0 16px 40px rgba(10,37,64,0.10)',
              aspectRatio: '4 / 5',
              background: PALETTE.bgSoft,
            }}>
              <img
                src="/team/asif.jpg"
                alt="Asif Khan, Founder, CloudVoro"
                style={{
                  display: 'block',
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                }}
              />
              <div style={{
                position: 'absolute',
                left: 0, right: 0, bottom: 0,
                padding: '14px 18px',
                background: 'linear-gradient(180deg, transparent 0%, rgba(10,37,64,0.92) 100%)',
                color: '#fff',
              }}>
                <div style={{
                  fontSize: 9.5,
                  letterSpacing: '0.18em',
                  textTransform: 'uppercase',
                  fontWeight: 700,
                  color: PALETTE.gold,
                }}>Asif Khan · Founder</div>
                <div style={{
                  fontSize: 13,
                  marginTop: 3,
                  fontStyle: 'italic',
                  color: '#E5E7EB',
                }}>Intellix IT Solutions (UK) · CloudVoro (Ireland) · MSc, Maynooth</div>
              </div>
            </div>
          </div>

          {/* Copy */}
          <div>
            <div style={{
              fontSize: 11,
              letterSpacing: '0.22em',
              textTransform: 'uppercase',
              fontWeight: 700,
              color: PALETTE.magenta,
              marginBottom: 14,
            }}>Built by a team you can phone</div>
            <h2 style={{
              fontFamily: "'Playfair Display', Georgia, serif",
              fontSize: 'clamp(28px, 3.6vw, 42px)',
              fontWeight: 700,
              color: PALETTE.navy,
              margin: 0,
              letterSpacing: '-0.02em',
              lineHeight: 1.1,
            }}>
              Hi, I'm Asif. I've spent 15+ years<br/>
              <em style={{ fontStyle: 'italic', fontWeight: 500, color: PALETTE.magenta }}>fixing other people's IT.</em>
            </h2>
            <div style={{
              fontSize: 12,
              letterSpacing: '0.14em',
              textTransform: 'uppercase',
              fontWeight: 600,
              color: PALETTE.muted,
              marginTop: 14,
              lineHeight: 1.5,
            }}>
              Founder, Keystone & CloudVoro (Ireland)  ·  Director & Principal Consultant, Intellix IT Solutions (UK)  ·  MSc, Maynooth University
            </div>
            <p style={{
              fontSize: 16,
              lineHeight: 1.65,
              color: PALETTE.slate,
              marginTop: 22,
            }}>
              I've spent 15+ years cleaning up other people's IT, for clients who can't afford for things to go wrong. Same playbook every time: cut the friction, harden the ops, then get out of the way.
            </p>
            <p style={{
              fontSize: 15,
              lineHeight: 1.6,
              color: PALETTE.slate,
              marginTop: 14,
            }}>
              Keystone exists because I kept watching brilliant food producers across Europe run on spreadsheets, paying €40k+ for ERPs that didn't fit, and being told <em>"that's just how it is."</em> You get a direct line to the team that builds Keystone, senior people from the first call. No sales funnel. No ticket queue. And me on the phone when it matters.
            </p>
            <div style={{ marginTop: 10 }}>
              <a
                href="/site/about"
                style={{
                  fontSize: 13,
                  fontWeight: 600,
                  color: PALETTE.magenta,
                  textDecoration: 'none',
                  letterSpacing: '0.04em',
                }}
                data-testid="founder-full-bio-link"
              >
                Read the full bio →
              </a>
            </div>

            <div style={{
              marginTop: 28,
              paddingTop: 22,
              borderTop: `1px solid ${PALETTE.border}`,
            }}>
              <div style={{
                fontSize: 10.5,
                letterSpacing: '0.18em',
                textTransform: 'uppercase',
                fontWeight: 700,
                color: PALETTE.muted,
                marginBottom: 12,
              }}>Affiliations & professional memberships</div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16, alignItems: 'center' }}>
                {affiliations.map((a) => (
                  <img
                    key={a.label}
                    src={a.src}
                    alt={a.label}
                    title={a.label}
                    style={{
                      height: 40,
                      width: 'auto',
                      objectFit: 'contain',
                      filter: 'grayscale(0.2)',
                      opacity: 0.92,
                    }}
                  />
                ))}
              </div>
            </div>
          </div>
        </div>

        <style>{`
          @media (max-width: 900px) {
            .founder-row { grid-template-columns: 1fr !important; gap: 40px !important; }
            .founder-photo-wrap { max-width: 280px; margin: 0 auto; }
          }
        `}</style>
      </div>
    </section>
  );
}
